// PORTED COPY — A20 phase 3 (C3). Original: TPRM7
// packages/ui/src/reporter/ReportableBoundary.tsx (@vendorwatch/ui). The licence
// repo cannot import that package — separate repo, separate lockfile, a Vite
// alias of '@' → './src' — so it carries a copy, exactly as types.ts /
// strings.ts / useRecorder.ts / ReportProvider.tsx / ReportDialog.tsx already do.
// Keep it diffable against the original; behaviour changes belong upstream first.
//
// TWO deliberate divergences, each named where it happens:
//   1. `'use client'` is dropped (Vite SPA, no RSC boundary).
//   2. This file ALSO carries `AppErrorBoundary`, the root screen. Upstream has
//      no equivalent because Next supplies app/global-error.tsx for that job;
//      a Vite SPA has nothing, so without this a crash above the provider is a
//      white page with no words on it at all.
//
// ═══════════════════════════════════════════════════════════════════════════
// WHY THIS FILE EXISTS AT ALL, AND WHERE ITS TWO BOUNDARIES MUST SIT
//
// The reporter keeps a rolling capture buffer inside <ReportProvider>. A React
// error boundary UNMOUNTS the subtree it catches. So a boundary placed ABOVE the
// provider destroys the very buffer the report is for: the user is offered
// "report this" and the seconds that led to the crash are already gone.
//
//     ✗  <Boundary><ConsoleReporter>{app}</ConsoleReporter></Boundary>
//     ✓  <ConsoleReporter><Boundary>{app}</Boundary></ConsoleReporter>
//
// Next arranges this for you (app/error.tsx renders inside app/layout.tsx). A
// Vite SPA does not, and this console had the provider mounted DEEP — inside
// AdminHeader, three levels below the router — where no boundary above the
// routed content could ever see it. App.tsx therefore hoists the provider to the
// shell (see PortalShell there) so that:
//
//   · ReportableBoundary sits INSIDE it, around the routed page: a page crash
//     leaves the header, the sidebar, the report button AND the buffer alive.
//   · AppErrorBoundary sits OUTSIDE everything, catching what the first one
//     cannot — a crash in the chrome, the session gate or the provider itself.
//     There is no buffer to attach at that point and this screen says so,
//     offering a pre-filled email instead of a button that would attach nothing.
//
// The honesty rule is the same one `capture_available` follows on a ticket:
// never claim a recording that does not exist.
// ═══════════════════════════════════════════════════════════════════════════

import * as React from 'react';
import { PRODUCT_NAME, SUPPORT_EMAIL } from '@/lib/brand';
import { useReporterOptional } from './ReportProvider';

// Caps. The dialog's textarea declares maxLength=5000, but a programmatic value
// set bypasses that, so the ceiling has to be enforced here.
const MAX_STACK_CHARS = 2000;
const MAX_DESCRIPTION_CHARS = 4500;
const MAX_TITLE_CHARS = 160;
/** Keep the mailto URL well inside what mail clients accept. */
const MAX_MAILTO_BODY = 900;

/** How long to keep looking for the dialog's fields after opening it. */
const PREFILL_TIMEOUT_MS = 1500;

const PREFILL_NOTE =
  'Please add what you were doing when this happened — the technical detail below was filled in automatically.';

export interface CaughtErrorContext {
  /** Defaults to the current pathname. */
  route?: string;
  componentStack?: string;
}

function truncate(value: string, max: number): string {
  return value.length <= max ? value : `${value.slice(0, max)}\n… (truncated)`;
}

function currentRoute(): string {
  if (typeof location === 'undefined') return '';
  return location.pathname || '/';
}

/** "TypeError: x is not a function", or a best-effort string for a thrown non-Error. */
export function errorMessageOf(error: unknown): string {
  if (error instanceof Error) return `${error.name}: ${error.message}`;
  if (typeof error === 'string' && error) return error;
  return 'Unknown error';
}

/**
 * Render a caught error as the body of a report.
 *
 * Message and stack as TEXT, deliberately not the error object: JSON.stringify
 * on an Error yields "{}" and String(err) drops the stack entirely.
 */
export function formatErrorForReport(error: unknown, extra?: CaughtErrorContext): string {
  const stack =
    error instanceof Error && error.stack ? truncate(error.stack, MAX_STACK_CHARS) : null;
  const componentStack = extra?.componentStack
    ? truncate(extra.componentStack, MAX_STACK_CHARS)
    : null;

  const lines = [
    PREFILL_NOTE,
    '',
    `Route: ${extra?.route ?? currentRoute()}`,
    `Error: ${errorMessageOf(error)}`,
  ];
  if (stack) lines.push('', 'Stack:', stack);
  if (componentStack) lines.push('', 'Component stack:', componentStack.trim());

  return truncate(lines.join('\n'), MAX_DESCRIPTION_CHARS);
}

function reportTitleFor(error: unknown, extra?: CaughtErrorContext): string {
  return `Crash on ${extra?.route ?? currentRoute()} — ${errorMessageOf(error)}`.slice(
    0,
    MAX_TITLE_CHARS,
  );
}

/**
 * Write a value into a React-controlled field the way a user would.
 *
 * React tracks the previous value on the DOM node, so assigning `el.value`
 * directly is swallowed. Going through the prototype setter bypasses that
 * tracker, and the bubbled `input` event is what React's root listener turns
 * into the component's onChange.
 */
function setReactValue(el: HTMLInputElement | HTMLTextAreaElement, value: string): void {
  const proto =
    el instanceof HTMLTextAreaElement ? HTMLTextAreaElement.prototype : HTMLInputElement.prototype;
  const setter = Object.getOwnPropertyDescriptor(proto, 'value')?.set;
  if (setter) setter.call(el, value);
  else el.value = value;
  el.dispatchEvent(new Event('input', { bubbles: true }));
}

/**
 * Fill the dialog's fields once it mounts.
 *
 * The dialog owns its own form state and exposes no prefill API, so rather than
 * widen that surface for one caller we fill it through the DOM. Every step is
 * optional and the whole thing is swallowed: if the dialog's markup changes the
 * user gets an empty form, which is what they would have had anyway. An error
 * screen that throws is the worst possible outcome.
 */
function prefillReportDialog(values: { title: string; description: string }): void {
  if (typeof window === 'undefined') return;
  const deadline = Date.now() + PREFILL_TIMEOUT_MS;

  const tick = () => {
    try {
      const description =
        document.querySelector<HTMLTextAreaElement>('#ps-report-desc') ??
        document.querySelector<HTMLTextAreaElement>('[role="dialog"] textarea');

      if (!description) {
        if (Date.now() < deadline) window.requestAnimationFrame(tick);
        return;
      }

      // Never clobber a draft — the user may have reopened a report they started.
      if (description.value === '') setReactValue(description, values.description);
      const title = document.querySelector<HTMLInputElement>('#ps-report-title');
      if (title && title.value === '') setReactValue(title, values.title);
    } catch {
      /* prefill is a convenience; the dialog is fully usable without it */
    }
  };

  window.requestAnimationFrame(tick);
}

/**
 * Open the reporter pre-filled from a caught error.
 *
 * `canReport` is false when no <ReportProvider> is above this component. The
 * caller MUST then say so rather than draw a button that does nothing.
 */
export function useErrorReporter(): {
  canReport: boolean;
  reportError: (error: unknown, extra?: CaughtErrorContext) => void;
} {
  const reporter = useReporterOptional();

  const reportError = React.useCallback(
    (error: unknown, extra?: CaughtErrorContext) => {
      if (!reporter) return;
      reporter.setOpen(true, 'report');
      prefillReportDialog({
        title: reportTitleFor(error, extra),
        description: formatErrorForReport(error, extra),
      });
    },
    [reporter],
  );

  return { canReport: reporter !== null, reportError };
}

// ── Shared presentation ────────────────────────────────────────────────────

const panel: React.CSSProperties = {
  borderRadius: 12,
  border: '1px solid var(--border)',
  background: 'var(--surface)',
  padding: 20,
};

const detailBox: React.CSSProperties = {
  borderRadius: 8,
  border: '1px solid #FECACA',
  background: '#FEF2F2',
  color: '#7F1D1D',
  padding: '12px 14px',
  fontFamily: 'var(--mono, ui-monospace, monospace)',
  fontSize: 11.5,
  lineHeight: 1.6,
  wordBreak: 'break-word',
};

const btn: React.CSSProperties = {
  borderRadius: 8,
  border: '1px solid var(--border)',
  background: 'var(--surface)',
  color: 'var(--text-secondary)',
  padding: '7px 14px',
  fontSize: 12,
  fontWeight: 600,
  fontFamily: 'inherit',
  textDecoration: 'none',
  display: 'inline-block',
  cursor: 'pointer',
};

const btnPrimary: React.CSSProperties = {
  ...btn,
  background: 'var(--accent)',
  borderColor: 'var(--accent)',
  color: '#fff',
};

function TechnicalDetail({ text }: { text: string }) {
  return (
    <details style={{ marginTop: 14 }}>
      <summary
        style={{ cursor: 'pointer', fontSize: 12, fontWeight: 600, color: 'var(--text-muted)' }}
      >
        Technical detail
      </summary>
      <pre
        style={{
          margin: '8px 0 0',
          maxHeight: 260,
          overflow: 'auto',
          whiteSpace: 'pre-wrap',
          borderRadius: 8,
          border: '1px solid var(--border)',
          background: 'var(--bg-muted)',
          color: 'var(--text-secondary)',
          padding: 12,
          fontFamily: 'var(--mono, ui-monospace, monospace)',
          fontSize: 11,
          lineHeight: 1.6,
        }}
      >
        {text}
      </pre>
    </details>
  );
}

/**
 * The subtree fallback: what broke, a retry, and a report that carries the
 * buffer. Split from the class so it can use hooks.
 *
 * When `canReport` is false the button is REPLACED by a sentence, not hidden. A
 * missing affordance reads as an oversight; the sentence is the truth.
 */
function CrashPanel({
  error,
  componentStack,
  title,
  onRetry,
}: {
  error: unknown;
  componentStack?: string;
  title?: string;
  onRetry: () => void;
}) {
  const { canReport, reportError } = useErrorReporter();

  return (
    <div role="alert" style={panel}>
      <div style={{ fontSize: 15, fontWeight: 600, color: 'var(--text-primary)' }}>
        {title ?? 'Something on this page stopped working'}
      </div>
      <p
        style={{
          margin: '6px 0 14px',
          fontSize: 13,
          lineHeight: 1.6,
          color: 'var(--text-secondary)',
        }}
      >
        Trying again often works. If it doesn&rsquo;t, send us a report — it carries the last part of
        this session, so we get what led up to the failure and not only its message.
      </p>

      <div style={detailBox}>{errorMessageOf(error)}</div>

      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginTop: 14 }}>
        <button type="button" onClick={onRetry} style={btn}>
          Try again
        </button>
        {canReport ? (
          <button
            type="button"
            onClick={() => reportError(error, { componentStack })}
            style={btn}
          >
            Report this problem
          </button>
        ) : (
          <span style={{ fontSize: 12, color: 'var(--text-muted)', alignSelf: 'center' }}>
            This screen sits outside the in-app reporter, so it can&rsquo;t attach a session
            recording.
          </span>
        )}
      </div>

      <TechnicalDetail text={formatErrorForReport(error, { componentStack })} />
    </div>
  );
}

// ── The subtree boundary ───────────────────────────────────────────────────
// Class component because getDerivedStateFromError / componentDidCatch have no
// hook equivalent.

interface BoundaryProps {
  children: React.ReactNode;
  title?: string;
  /** Runs after the boundary clears, e.g. to refetch the data that blew up. */
  onReset?: () => void;
}

interface BoundaryState {
  error: unknown;
  componentStack?: string;
}

export class ReportableBoundary extends React.Component<BoundaryProps, BoundaryState> {
  state: BoundaryState = { error: null };

  static getDerivedStateFromError(error: unknown): Partial<BoundaryState> {
    // `throw null` is legal JS. Coerce so the fallback actually renders instead
    // of the boundary deciding it is healthy and re-rendering the same crash.
    return { error: error ?? new Error('Unknown error') };
  }

  componentDidCatch(error: Error, info: React.ErrorInfo) {
    // Also lands in the reporter's rolling console buffer, so a report filed
    // moments later carries this even if the user never opens the panel.
    console.error('[console] subtree error', error, info.componentStack);
    this.setState({ componentStack: info.componentStack ?? undefined });
  }

  private reset = () => {
    this.setState({ error: null, componentStack: undefined });
    this.props.onReset?.();
  };

  render() {
    if (this.state.error == null) return this.props.children;
    return (
      <CrashPanel
        error={this.state.error}
        componentStack={this.state.componentStack}
        title={this.props.title}
        onRetry={this.reset}
      />
    );
  }
}

// ── The root boundary ──────────────────────────────────────────────────────

/**
 * The whole-app fallback, for a crash the subtree boundary cannot reach: the
 * chrome, the session gate, the router, or the reporter provider itself.
 *
 * It deliberately does NOT try to open the reporter. By the time this renders,
 * whatever held the capture buffer is either unmounted or is the thing that
 * threw, so a "report" button here would attach nothing while implying it had.
 * A pre-filled mailto is the honest substitute — and, unlike the button, it
 * still works when the app is too broken to render anything else.
 *
 * Kept dependency-free on purpose (two brand consts, inline styles, no router):
 * a crash screen that throws is the worst possible outcome, and this one has no
 * net beneath it.
 */
class RootBoundaryInner extends React.Component<{ children: React.ReactNode }, BoundaryState> {
  state: BoundaryState = { error: null };

  static getDerivedStateFromError(error: unknown): Partial<BoundaryState> {
    return { error: error ?? new Error('Unknown error') };
  }

  componentDidCatch(error: Error, info: React.ErrorInfo) {
    console.error('[console] fatal error', error, info.componentStack);
    this.setState({ componentStack: info.componentStack ?? undefined });
  }

  render() {
    if (this.state.error == null) return this.props.children;

    const detail = formatErrorForReport(this.state.error, {
      componentStack: this.state.componentStack,
    });
    const body = [
      'Please describe what you were doing when this happened:',
      '',
      '',
      '--- technical detail ---',
      `Route: ${currentRoute()}`,
      `Error: ${errorMessageOf(this.state.error)}`,
      typeof navigator !== 'undefined' ? `Browser: ${navigator.userAgent}` : '',
    ]
      .filter(Boolean)
      .join('\n')
      .slice(0, MAX_MAILTO_BODY);

    const mailto = `mailto:${SUPPORT_EMAIL}?subject=${encodeURIComponent(
      `${PRODUCT_NAME} Console — application error`,
    )}&body=${encodeURIComponent(body)}`;

    return (
      <div
        role="alert"
        style={{
          minHeight: '100vh',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          padding: 24,
          background: 'var(--bg-secondary)',
        }}
      >
        <div style={{ ...panel, width: '100%', maxWidth: 560 }}>
          <h1 style={{ margin: 0, fontSize: 17, fontWeight: 600, color: 'var(--text-primary)' }}>
            {PRODUCT_NAME} couldn&rsquo;t load
          </h1>
          <p
            style={{
              margin: '6px 0 14px',
              fontSize: 13,
              lineHeight: 1.6,
              color: 'var(--text-secondary)',
            }}
          >
            The console failed before it finished starting up. Reloading usually clears it.
          </p>

          <div style={detailBox}>{errorMessageOf(this.state.error)}</div>

          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginTop: 14 }}>
            <button
              type="button"
              style={btnPrimary}
              onClick={() => window.location.reload()}
            >
              Reload
            </button>
            <a style={btn} href={mailto}>
              Email support
            </a>
          </div>

          <p style={{ margin: '12px 0 0', fontSize: 12, color: 'var(--text-muted)' }}>
            This screen sits outside the in-app reporter, so it can&rsquo;t attach a session
            recording — the email opens with the detail below already filled in.
          </p>

          <TechnicalDetail text={detail} />
        </div>
      </div>
    );
  }
}

export function AppErrorBoundary({ children }: { children: React.ReactNode }) {
  return <RootBoundaryInner>{children}</RootBoundaryInner>;
}
